import { GraphQLDirective, GraphQLEnumType, GraphQLInputObjectType, GraphQLInterfaceType, GraphQLList, GraphQLNonNull, GraphQLObjectType, GraphQLScalarType, GraphQLSchema, GraphQLUnionType, isEnumType, isInputObjectType, isInterfaceType, isIntrospectionType, isListType, isNonNullType, isObjectType, isScalarType, isSpecifiedDirective, isSpecifiedScalarType, isUnionType, } from "graphql";
export function transformSchema(schema, options) {
    const { trimDeprecated = false, trimDescriptions = false } = options;
    const schemaConfig = schema.toConfig();
    const typeMap = new Map();
    function maybeTrimDescription(obj) {
        if (!trimDescriptions)
            return obj;
        const { description, ...rest } = obj;
        return rest;
    }
    const remapType = (t) => {
        if (isNonNullType(t)) {
            return new GraphQLNonNull(remapType(t.ofType));
        }
        else if (isListType(t)) {
            return new GraphQLList(remapType(t.ofType));
        }
        else {
            return typeMap.get(t.name) ?? t;
        }
    };
    function remapNamedType(t) {
        return t ? (typeMap.get(t.name) ?? t) : undefined;
    }
    function remapArgs(args) {
        return args
            ? Object.fromEntries(Object.entries(args)
                .filter(([_argName, arg]) => !trimDeprecated || !arg.deprecationReason)
                .map(([argName, arg]) => [
                argName,
                maybeTrimDescription(replaceType(arg)),
            ]))
            : undefined;
    }
    function replaceType(config) {
        const { type, ...rest } = config;
        return { ...rest, type: remapType(type) };
    }
    function replaceArgs(f) {
        const { args, ...rest } = f;
        return { ...rest, args: remapArgs(args) };
    }
    function remapFields(oldFields) {
        const fields = Object.create(null);
        for (const [fname, f] of Object.entries(oldFields)) {
            if (trimDeprecated && f.deprecationReason != null)
                continue;
            fields[fname] = maybeTrimDescription(replaceType(replaceArgs(f)));
        }
        return fields;
    }
    function replaceFields(obj) {
        const { fields, ...rest } = obj;
        return { ...rest, fields: () => remapFields(fields) };
    }
    function remapInputFields(oldFields) {
        const fields = Object.create(null);
        for (const [fname, f] of Object.entries(oldFields)) {
            if (trimDeprecated && f.deprecationReason != null)
                continue;
            fields[fname] = maybeTrimDescription(replaceType(f));
        }
        return fields;
    }
    function replaceInputFields(obj) {
        const { fields, ...rest } = obj;
        return { ...rest, fields: () => remapInputFields(fields) };
    }
    function replaceInterfaces(obj) {
        const { interfaces, ...rest } = obj;
        return {
            ...rest,
            interfaces: () => interfaces.map((iface) => remapNamedType(iface)),
        };
    }
    function transformType(type) {
        switch (true) {
            case isScalarType(type): {
                return new GraphQLScalarType(maybeTrimDescription(type.toConfig()));
            }
            case isEnumType(type): {
                const config = type.toConfig();
                const values = Object.fromEntries(Object.entries(config.values)
                    .filter(([, v]) => !trimDeprecated || !v.deprecationReason)
                    .map(([k, v]) => [k, maybeTrimDescription(v)]));
                return new GraphQLEnumType(maybeTrimDescription({
                    ...config,
                    values,
                }));
            }
            case isUnionType(type): {
                const config = type.toConfig();
                return new GraphQLUnionType(maybeTrimDescription(maybeTrimDescription({
                    ...config,
                    types: () => config.types.map((t) => remapNamedType(t)),
                })));
            }
            case isInterfaceType(type): {
                const config = type.toConfig();
                return new GraphQLInterfaceType(maybeTrimDescription(replaceFields(replaceInterfaces(config))));
            }
            case isInputObjectType(type): {
                const config = type.toConfig();
                return new GraphQLInputObjectType(maybeTrimDescription(replaceInputFields(config)));
            }
            case isObjectType(type): {
                const config = type.toConfig();
                return new GraphQLObjectType(maybeTrimDescription(replaceFields(replaceInterfaces(config))));
            }
            default: {
                return type;
            }
        }
    }
    for (const t of schemaConfig.types) {
        if (typeMap.has(t.name))
            continue;
        if (isIntrospectionType(t))
            continue;
        if (isSpecifiedScalarType(t)) {
            typeMap.set(t.name, t);
        }
        else {
            const type = transformType(t);
            typeMap.set(t.name, type);
        }
    }
    const directives = schemaConfig.directives.map((d) => isSpecifiedDirective(d)
        ? d
        : new GraphQLDirective(maybeTrimDescription(replaceArgs(d.toConfig()))));
    return new GraphQLSchema(maybeTrimDescription({
        ...schemaConfig,
        types: Array.from(typeMap.values()),
        directives,
        query: remapNamedType(schemaConfig.query),
        mutation: remapNamedType(schemaConfig.mutation),
        subscription: remapNamedType(schemaConfig.subscription),
    }));
}
//# sourceMappingURL=transformSchema.js.map